import React from 'react';
import { X, AlertTriangle, LogIn, LogOut, Ban } from 'lucide-react';

interface OperationalConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  confirmButtonClass?: string;
  icon?: 'warning' | 'entry' | 'exit' | 'revoke';
  isProcessing?: boolean;
  showDisclaimer?: boolean;
  disclaimerType?: 'entry' | 'revoke' | 'general';
  operatorName?: string;
  requireAcknowledgment?: boolean;
}

export const OperationalConfirmModal: React.FC<OperationalConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  confirmButtonClass = 'bg-blue-600 hover:bg-blue-700',
  icon = 'warning',
  isProcessing = false,
  showDisclaimer = false,
  disclaimerType = 'general',
  operatorName,
  requireAcknowledgment = false,
}) => {
  const [acknowledged, setAcknowledged] = React.useState(false);

  // Reset acknowledgment when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const canConfirm = !isProcessing && (!requireAcknowledgment || acknowledged);
    if (e.key === 'Enter' && canConfirm) {
      onConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const getIcon = () => {
    switch (icon) {
      case 'entry':
        return <LogIn className="h-6 w-6 text-green-400" />;
      case 'exit':
        return <LogOut className="h-6 w-6 text-blue-400" />;
      case 'revoke':
        return <Ban className="h-6 w-6 text-red-400" />;
      case 'warning':
      default:
        return <AlertTriangle className="h-6 w-6 text-yellow-400" />;
    }
  };

  const getDisclaimerContent = () => {
    const currentTimestamp = new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    switch (disclaimerType) {
      case 'entry':
        return {
          title: 'AVISO DE SEGURANÇA E RESPONSABILIDADE',
          bgColor: 'bg-amber-900/20',
          borderColor: 'border-amber-600/50',
          textColor: 'text-amber-200',
          titleColor: 'text-amber-300',
          content: (
            <>
              <p className="mb-2">Ao autorizar a entrada manual, você declara que:</p>
              <ul className="space-y-1 mb-3">
                <li>• Verificou a identidade e autorização do visitante</li>
                <li>• Confirma que as informações fornecidas são verdadeiras</li>
                <li>• Está ciente que esta ação será registrada em seu nome</li>
                <li>• Assume total responsabilidade por qualquer irregularidade</li>
              </ul>
              <p className="mb-2">
                Esta autorização ficará registrada permanentemente no sistema vinculada ao seu usuário{' '}
                {operatorName && <span className="text-cyan-300 font-semibold">({operatorName})</span>} com data, hora e IP de acesso.
              </p>
              <p className="mb-2 text-xs text-amber-300">
                Registro será criado em: {currentTimestamp}
              </p>
              <p className="font-semibold text-amber-300">
                O acesso não autorizado a áreas restritas pode resultar em responsabilização civil e criminal do autorizador.
              </p>
            </>
          ),
        };
      case 'revoke':
        return {
          title: 'AVISO DE AUDITORIA',
          bgColor: 'bg-red-900/20',
          borderColor: 'border-red-600/50',
          textColor: 'text-red-200',
          titleColor: 'text-red-300',
          content: (
            <>
              <p className="mb-2">Esta ação de revogação será registrada com:</p>
              <ul className="space-y-1 mb-3">
                <li>
                  • Seu usuário: {operatorName && <span className="text-cyan-300 font-semibold">{operatorName}</span>}
                </li>
                <li>• Data e hora: {currentTimestamp}</li>
                <li>• Motivo: Revogação manual via painel operacional</li>
              </ul>
              <p className="font-semibold">
                A revogação imediata pode afetar o acesso de pessoas autorizadas. Certifique-se de que está revogando o acesso correto.
              </p>
            </>
          ),
        };
      case 'general':
      default:
        return {
          title: 'REGISTRO DE AUDITORIA',
          bgColor: 'bg-blue-900/20',
          borderColor: 'border-blue-600/50',
          textColor: 'text-blue-200',
          titleColor: 'text-blue-300',
          content: (
            <>
              <p className="mb-2">
                Esta ação será registrada no histórico do sistema para fins de auditoria e rastreabilidade.
              </p>
              <p className="text-xs">
                Operador: {operatorName && <span className="text-cyan-300 font-semibold">{operatorName}</span>}
                {' • '}
                Timestamp: {currentTimestamp}
              </p>
            </>
          ),
        };
    }
  };

  const disclaimerData = showDisclaimer ? getDisclaimerContent() : null;
  const isConfirmDisabled = isProcessing || (requireAcknowledgment && !acknowledged);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-[100] flex items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        className="relative bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-gray-800 bg-opacity-90 z-50 flex items-center justify-center rounded-xl">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-600 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-sm text-gray-300 font-medium">
                Processando...
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getIcon()}
            <h3 className="text-lg font-semibold text-white">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-300 transition-colors disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-gray-300 text-base leading-relaxed">
            {message}
          </p>
        </div>

        {/* Disclaimer Section */}
        {showDisclaimer && disclaimerData && (
          <div className="px-6 pb-6">
            <div className={`${disclaimerData.bgColor} ${disclaimerData.borderColor} border rounded-lg p-4`}>
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className={`h-5 w-5 ${disclaimerData.titleColor} flex-shrink-0 mt-0.5`} />
                <h4 className={`text-sm font-bold ${disclaimerData.titleColor} uppercase tracking-wide`}>
                  {disclaimerData.title}
                </h4>
              </div>
              <div className={`text-xs ${disclaimerData.textColor} leading-relaxed`}>
                {disclaimerData.content}
              </div>
              {requireAcknowledgment && (
                <div className="mt-4 pt-3 border-t border-gray-600">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-500 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-xs text-gray-300 group-hover:text-white transition-colors">
                      Li e concordo com os termos de responsabilidade acima
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-900 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isConfirmDisabled}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${confirmButtonClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
